/**
 * parts.tsx — byggeklosser til inNode-illustrasjonene (isometrisk SVG)
 *
 * CodeWindow: mørkt kodevindu med innrykkede linjer og markør. Pipeline: tre
 * kuber i rekke (bygg → test → deploy) med sjekkmerke på den siste. Gjenbruker
 * Rack fra inVPS-delene. Ingen Node.js-logo eller andre varemerker. Kun blå og grå.
 */

import { Box, iso as heroIso } from '@/components/product/iso';
import { Cube } from '@/components/invps/parts';

type Project = (x: number, y: number, z: number) => string;

interface CodeWindowProps {
  x: number;
  y: number;
  z?: number;
  project?: Project;
}

/** [innrykk, lengde, farge] per kodelinje (blå = nøkkelord, grå = vanlig kode) */
const LINES: [number, number, string][] = [
  [0.08, 0.36, '#78a9ff'],
  [0.16, 0.5, '#c6c6c6'],
  [0.16, 0.32, '#78a9ff'],
  [0.24, 0.46, '#8d8d8d'],
  [0.16, 0.28, '#c6c6c6'],
  [0.08, 0.18, '#78a9ff'],
];

export function CodeWindow({ x, y, z = 0, project = heroIso }: CodeWindowProps) {
  const w = 3.8;
  const d = 2.8;
  const h = 0.24;
  const zt = z + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  const rect = (u0: number, v0: number, u1: number, v1: number, fill: string, key: string, opacity = 1) => (
    <polygon key={key} points={`${p(u0, v0)} ${p(u1, v0)} ${p(u1, v1)} ${p(u0, v1)}`} fill={fill} opacity={opacity} />
  );
  return (
    <g>
      <Box x={x} y={y} z={z} w={w} d={d} h={h} colors={['#393939', '#262626', '#161616']} project={project} />
      {rect(0, 0, 1, 0.16, '#525252', 'bar')}
      {[0.05, 0.1, 0.15].map((u, i) => rect(u, 0.055, u + 0.03, 0.105, i === 0 ? '#78a9ff' : '#8d8d8d', `dot${i}`))}
      {LINES.map(([indent, len, color], i) => {
        const v = 0.26 + i * 0.115;
        return rect(indent, v, Math.min(0.95, indent + len), v + 0.05, color, `l${i}`, 0.95);
      })}
      {/* markør */}
      {rect(0.32, 0.26 + 5 * 0.115, 0.36, 0.26 + 5 * 0.115 + 0.05, '#ffffff', 'cursor')}
    </g>
  );
}

/** Deploy-kjede: tre kuber i rekke med økende blåtone, og et sjekkmerke over den siste */
export function Pipeline({ x, y, z = 0, project = heroIso }: CodeWindowProps) {
  return (
    <g>
      <Cube x={x} y={y} z={z} size={1.1} tone="light" project={project} />
      <Cube x={x + 1.7} y={y} z={z} size={1.1} tone="blue" project={project} />
      <Cube x={x + 3.4} y={y} z={z} size={1.1} tone="deep" project={project} />
    </g>
  );
}
